import { ArrowDown, ArrowUp, ArrowUpDown, Pencil } from "lucide-react";
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { MultiFilterDropdown, type MultiFilterMode, type MultiFilterOption, NzrAvatar } from "nazare-ui/components";
import { PageSection } from "nazare-ui/components/page-section";
import { Tooltip, TooltipContent, TooltipTrigger } from "nazare-ui/primitives";
import { cn } from "nazare-ui/utils";

import {
  CHANNEL_TYPES,
  useBulkApplyPreference,
  useMcpEventsEnabled,
  useSearchUsers,
  useSetPreferenceForUser,
  useUsersChannels,
  useUsersPreferences,
  type ChannelType,
  type NotificationUser,
} from "../../../notifications-admin-api";
import { sanitiseChannelsParam, visibleChannels } from "../../../notification-channel-visibility";
import { usePageSearch } from "../../../global-search-context";
import { LoadingBar } from "../../../loading-bar";
import { ScrollShadow } from "../../../scroll-shadow";
import { useSecondaryHeader } from "../../../secondary-header-context";
import { SkeletonBlock } from "../../../skeleton-block";
import { useDocumentTitle } from "../../../use-document-title";
import { useScrollEdges } from "../../../use-scroll-edges";
import { BulkEditModal, type BulkEditPair } from "./bulk-edit-modal";
import {
  CHANNEL_ICON,
  CHANNEL_LABEL,
  FLAT_TYPES,
  needsSpacerBefore,
  NOTIFICATION_TYPE_GROUPS,
} from "./notification-catalogue";
import styles from "./index.module.css";

/** Total rendered height of this matrix's two-row `<thead>` (group row +
 * type row, 58px each — confirmed live: 116px) — this table's own header
 * shape, distinct from the shared single-row `TABLE_HEADER_HEIGHT` other
 * (`MaximisedTablePage`-based) tables use. Passed to `<ScrollShadow
 * topOffset={...}>` so the top shadow sits right below the real header
 * instead of overlapping it. */
const MATRIX_HEADER_HEIGHT = 116;

/** Matches `.channelBadge`'s own 30px (index.module.css) — the user
 * column's avatar reads as part of the same row of icon-sized controls as
 * every channel badge next to it, not a separate, larger visual weight. */
const CHANNEL_BADGE_SIZE = 30;

/**
 * `/notifications` — admin surface to bulk-view/edit any user's notification
 * settings. Backed by the `notifications-admin` BFF worktree's
 * `/notifications/admin/users/*` routes (see `../../../notifications-admin-api.ts`).
 *
 * The matrix IS the page: search (via the shell's ONE global search box —
 * see `usePageSearch`, never a second local input) narrows which users are
 * rows; every notification TYPE is its own column (see
 * `NOTIFICATION_TYPE_GROUPS` below), and each cell holds one small icon
 * toggle per channel that type actually supports — a compact "what's on for
 * this type" widget rather than a separate column per (type, channel) pair,
 * which got unreadably wide (~27 columns) for only 10 real types.
 */

/** The BFF's `preferences/:type/:refId/:channel` route treats the literal
 * string `"_"` as its null-reference_id sentinel (path segments can't be
 * empty) — see `admin-users.ts`'s own comment. */
const NULL_REF_ID = "_";

function userDisplayName(user: NotificationUser): string {
  const full = [user.first_name, user.last_name].filter(Boolean).join(" ");
  return full || user.email;
}

/**
 * The matrix's two sticky header rows (group + type) — shared by both the
 * real, loaded `<table>` and the loading skeleton's `<table>` (see
 * `AdminNotificationsPage` below) so the skeleton's header is pixel-
 * identical to the real one that replaces it, not a second, independently
 * drifting copy. `selectAllDisabled` covers the skeleton case: no users
 * have loaded yet, so the control is disabled rather than hidden — same
 * "still real, not skeletonised" treatment every other header cell gets.
 *
 * `selectionSummary` ("N users" / "N of M selected") lives in `.userTh`
 * (the blank, `rowSpan={2}` cell above the user column), not `.checkboxTh`
 * — it used to sit alone in the shell's secondary header, disconnected
 * from the control it describes; putting it in the checkbox column instead
 * was tried next and was wrong too, since that column has no fixed `width`
 * of its own and grows/shrinks to fit whichever is widest (the bare
 * checkbox vs. checkbox+text), visibly resizing the whole column on every
 * count change. `.userTh` already has a stable width — pinned via the
 * `.userTh`/`.userTd` shrink-to-content rule in index.module.css, driven by
 * the widest actual user name/email in the column, not by this text — so
 * the summary can never widen it. `null` (the skeleton's case — no real
 * count exists yet) omits it entirely rather than rendering an empty
 * string.
 */
function MatrixHead({
  allSelected,
  indeterminate = false,
  onToggleSelectAll,
  selectAllDisabled = false,
  selectionSummary = null,
  sortDirection = null,
  onToggleSort,
}: {
  allSelected: boolean;
  /** Some but not all currently-visible users are selected — rendered as the
   * checkbox's native indeterminate dash. There's no JSX prop for this (it's
   * a DOM property, not a real HTML attribute React can serialize), so it's
   * set imperatively via `selectAllRef` below. */
  indeterminate?: boolean;
  onToggleSelectAll: () => void;
  selectAllDisabled?: boolean;
  selectionSummary?: string | null;
  /** `null` (the skeleton's case, same reasoning as `selectionSummary`
   * above) renders the sort button disabled rather than omitting it — the
   * column header shape shouldn't shift once real data loads. */
  sortDirection?: SortDirection | null;
  onToggleSort?: () => void;
}) {
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <thead className={styles.thead}>
      <tr>
        {/* Blank — the select-all checkbox lives one row down, at the same
            level as the rest of the real column headers, not spanning into
            this group-label row. */}
        <th className={cn(styles.th, styles.checkboxTh)} />
        <th className={cn(styles.th, styles.userTh, styles.thBottomBorder)} rowSpan={2}>
          <div className={styles.userThInner}>
            <button
              type="button"
              className={styles.userSortButton}
              onClick={onToggleSort}
              disabled={!onToggleSort}
              aria-label={
                sortDirection === "asc"
                  ? "Sorted A–Z — click to sort Z–A"
                  : sortDirection === "desc"
                    ? "Sorted Z–A — click to clear sort"
                    : "Sort by name"
              }
            >
              {sortDirection === "asc" && <ArrowUp className={styles.sortIcon} aria-hidden="true" />}
              {sortDirection === "desc" && <ArrowDown className={styles.sortIcon} aria-hidden="true" />}
              {sortDirection === null && <ArrowUpDown className={styles.sortIcon} aria-hidden="true" />}
            </button>
            {selectionSummary !== null && (
              <span className={styles.selectionSummary}>{selectionSummary}</span>
            )}
          </div>
        </th>
        {NOTIFICATION_TYPE_GROUPS.map((group, groupIndex) => (
          <Fragment key={group.group}>
            {groupIndex > 0 && <th className={styles.spacer} />}
            <th
              className={cn(styles.groupTh, groupIndex % 2 === 1 && styles.altColumn)}
              // *3: every type is 3 physical columns in the body rows below
              // (gap, indicator, gap — see the `.innerGap` cells further
              // down) — this header has to span all of them, or the
              // group's shaded block would fall short of its own columns.
              colSpan={group.types.length * 3}
              scope="colgroup"
            >
              {group.group}
            </th>
          </Fragment>
        ))}
        {/* Blank — spans both header rows (like the blank user-column cell
            above), same reasoning as `.trailingFill` itself: this is the
            one column that grows to fill a wide viewport, so every real
            column and `.spacer` can stay a fixed, left-aligned width.
            `.thBottomBorder` too — same as `.userTh` above: without it the
            header/body boundary line stopped short of the table's right
            edge instead of running the full width. */}
        <th className={cn(styles.trailingFill, styles.thBottomBorder)} rowSpan={2} />
      </tr>
      <tr>
        <th className={cn(styles.th, styles.checkboxTh, styles.thBottomBorder)}>
          <input
            ref={selectAllRef}
            type="checkbox"
            className={styles.checkbox}
            checked={allSelected}
            onChange={onToggleSelectAll}
            disabled={selectAllDisabled}
            aria-label="Select all users"
          />
        </th>
        {FLAT_TYPES.map((type) => (
          <Fragment key={type.type}>
            {needsSpacerBefore(type) && (
              <th className={cn(styles.spacer, styles.thBottomBorder)} />
            )}
            <th
              // Spans its own flanking gap columns (see the `.innerGap`
              // `<td>`s in the body rows below) so they never render as
              // their own `<th>` here.
              colSpan={3}
              className={cn(
                styles.th,
                styles.thBottomBorder,
                type.groupIndex % 2 === 1 && styles.altColumn,
                type.isGroupStart && styles.groupStart,
              )}
            >
              {type.label}
            </th>
          </Fragment>
        ))}
      </tr>
    </thead>
  );
}

/** Every real type, flattened with its group name prefixed — static, unlike
 * the old cascading version this replaced (which narrowed the CHANNEL list
 * to whatever single type was picked): with multi-select there's no longer
 * one type driving a single channel list, so type and channel options are
 * each independent and unfiltered by the other now. */
const ALL_TYPE_OPTIONS: MultiFilterOption<string>[] = FLAT_TYPES.map((type) => ({
  value: type.type,
  label: `${type.group} — ${type.label}`,
}));

function channelOptions(mcpEventsEnabled: boolean): MultiFilterOption<ChannelType>[] {
  return visibleChannels(CHANNEL_TYPES, mcpEventsEnabled).map((channel) => ({
    value: channel,
    label: CHANNEL_LABEL[channel],
  }));
}

/** "Any type" (nothing picked) / "1 type" / "N types" — same shape for the
 * channel trigger's label. Deliberately doesn't list the picked labels
 * themselves (could get long fast with several picks) — the dropdown's own
 * open state is where you see which ones and their include/exclude marks. */
function filterTriggerLabel(count: number, noun: string): string {
  if (count === 0) return `Any ${noun}`;
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/*
 * The whole filter+sort state (`filterTypes`/`filterChannels`/user-column
 * sort) lives in the URL's query string, not local `useState` — per live
 * feedback: a filtered/sorted view is worth linking to, sharing, and
 * surviving a reload/back-navigation, none of which local state supports.
 * Follows the same `useSearchParams` + `{ replace: true }` convention
 * `../qa-automation/pages/run-detail.tsx`'s `verdictFilter` already
 * established in this app (`replace: true` so every filter/sort tweak
 * doesn't spam browser history — only real navigations should be
 * back-button stops).
 *
 * `types`/`channels` params: comma-separated `value:mode` pairs (e.g.
 * `types=report_new_rows:include,scheduled_runs:exclude`) — a plain `Map`
 * doesn't survive round-tripping through a query string on its own, so
 * these encode/decode functions are the ONLY place that shape conversion
 * happens; every other piece of code in this file just deals in `Map`s.
 */
function encodeFilterMap<T extends string>(map: Map<T, MultiFilterMode>): string {
  return Array.from(map.entries())
    .map(([value, mode]) => `${value}:${mode}`)
    .join(",");
}

function decodeFilterMap<T extends string>(param: string | null): Map<T, MultiFilterMode> {
  const map = new Map<T, MultiFilterMode>();
  if (!param) return map;
  for (const entry of param.split(",")) {
    const [value, mode] = entry.split(":");
    if (value && (mode === "include" || mode === "exclude")) map.set(value as T, mode);
  }
  return map;
}

/** Only the user (name) column is sortable today — `sort` is still
 * `"<column>:<direction>"` shaped (not just a bare direction) so adding a
 * second sortable column later doesn't need a different param, just a
 * different `<column>` value here. */
type SortDirection = "asc" | "desc";
const SORT_COLUMN_NAME = "name";

function decodeSort(param: string | null): SortDirection | null {
  if (!param) return null;
  const [column, direction] = param.split(":");
  if (column !== SORT_COLUMN_NAME) return null;
  return direction === "asc" || direction === "desc" ? direction : null;
}

/** Cycles unsorted → ascending → descending → unsorted — same 3-state
 * shape the shared filter control uses for a picked filter option, so every
 * clickable header/dropdown control in this file cycles the same way. */
function cycleSort(current: SortDirection | null): SortDirection | null {
  if (current === null) return "asc";
  if (current === "asc") return "desc";
  return null;
}

/** Same `color-mix` expression as `.altColumn` in index.module.css —
 * duplicated as a plain string (not read from the CSS class) because this
 * paints a `linear-gradient()` computed in JS, which needs the literal
 * color value/expression to put in a gradient stop; `var()`/`color-mix()`
 * inside that string are still resolved by the BROWSER when it parses the
 * inline `style`, not by this code, so it stays theme-aware (light/dark)
 * exactly like the real cells it's extending. Keep in sync with
 * `.altColumn` if that ever changes. */
const GROUP_SHADE_COLOR = "color-mix(in srgb, var(--ink) 2.5%, var(--nzr-card))";

/**
 * Real `<tr>`s can never be forced to fill a scroll container's remaining
 * height (see `.table`'s own doc comment in index.module.css for the bug
 * that came from trying) — so instead of a `.fillerRow` INSIDE the table,
 * this measures the CURRENT table's group-header (`th[scope="colgroup"]`)
 * boundaries and paints a plain `<div>` AFTER the table with a
 * `linear-gradient` reproducing the same alternating shading at the same
 * x-offsets, extending down to fill whatever space real rows don't use
 * (that div is a FLEX child of `.matrixScroll` — `flex: 1 1 auto` reliably
 * absorbs leftover space the way table row height distribution here does
 * not). Re-measures on every `dependencyKey` change (pass something that
 * changes whenever the table's own content/column widths could — the
 * `users` array reference is enough, since a real re-fetch always yields a
 * new array) and on the container's own resize (a `ResizeObserver`, for a
 * plain viewport-width change with no data change).
 */
function useGroupShadingFiller(
  containerRef: React.RefObject<HTMLElement | null>,
  dependencyKey: unknown,
): { background: string; width: number | undefined } {
  const [state, setState] = useState<{ background: string; width: number | undefined }>({
    background: "none",
    width: undefined,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const table = container.querySelector("table");
      const groupThs = table
        ? Array.from(table.querySelectorAll('th[scope="colgroup"]'))
        : [];
      if (!table || groupThs.length === 0) {
        setState({ background: "none", width: undefined });
        return;
      }
      const stops: string[] = [];
      let cursor = 0;

      // The pinned user column's own `border-right` (see `.userTh`/`.userTd`
      // in index.module.css) stops at the last real row too — a 1px
      // border-colored hard stop at its right edge continues that vertical
      // divider down through the filler, the same way the shading below
      // continues the group columns' background.
      const userTh = table.querySelector(`.${styles.userTh}`) as HTMLElement | null;
      if (userTh) {
        const borderRight = userTh.offsetLeft + userTh.offsetWidth;
        stops.push(
          `transparent ${cursor}px`,
          `transparent ${borderRight - 1}px`,
          `var(--nzr-border) ${borderRight - 1}px`,
          `var(--nzr-border) ${borderRight}px`,
        );
        cursor = borderRight;
      }

      groupThs.forEach((th, groupIndex) => {
        const element = th as HTMLElement;
        const left = element.offsetLeft;
        const right = left + element.offsetWidth;
        // The gap BEFORE this group (a `.spacer` between groups, or between
        // the user column's border and the first group) is never shaded —
        // an explicit transparent pair of stops, not just omitted, because
        // a gradient otherwise LINEARLY INTERPOLATES between whatever
        // colors bound the gap instead of leaving it plain.
        if (left > cursor) stops.push(`transparent ${cursor}px`, `transparent ${left}px`);
        const shaded = groupIndex % 2 === 1;
        const color = shaded ? GROUP_SHADE_COLOR : "transparent";
        stops.push(`${color} ${left}px`, `${color} ${right}px`);
        cursor = right;
      });
      // Cuts the shading off right after the last group — without this, a
      // gradient's last color extends indefinitely past its final stop,
      // which would shade the `.trailingFill` column's whole width too.
      stops.push(`transparent ${cursor}px`);
      setState({
        background: `linear-gradient(to right, ${stops.join(", ")})`,
        width: table.offsetWidth,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-measures
    // on `dependencyKey` changing (a proxy for "the table's own content
    // might have changed") and on the container's own resize (the
    // ResizeObserver above) — `containerRef` itself is a stable ref object.
  }, [dependencyKey]);

  return state;
}

/**
 * One channel toggle icon in the matrix — split out and `memo`'d so an
 * unrelated re-render of `AdminNotificationsPage` (a different row's
 * selection, a different cell's pending state, a background refetch that
 * resolves to the exact same enabled/pending/connected values) doesn't
 * re-render every other badge along with it; React.memo's default shallow
 * prop comparison skips this component entirely when none of its own
 * primitive props actually changed. `onToggle` has to already be a STABLE
 * function reference (see `stableToggleChannel` below) for that comparison
 * to ever succeed — an inline arrow function recreated every parent render
 * would defeat the memo on every single render regardless of the other
 * props.
 */
const ChannelBadgeButton = memo(function ChannelBadgeButton({
  userId,
  type,
  channel,
  Icon,
  enabled,
  pending,
  connected,
  ariaLabel,
  tooltipLabel,
  onToggle,
}: {
  userId: string;
  type: string;
  channel: ChannelType;
  Icon: (typeof CHANNEL_ICON)[ChannelType];
  enabled: boolean;
  pending: boolean;
  connected: boolean;
  ariaLabel: string;
  tooltipLabel: string;
  onToggle: (userId: string, type: string, channel: ChannelType) => void;
}) {
  const handleClick = useCallback(
    () => onToggle(userId, type, channel),
    [onToggle, userId, type, channel],
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(styles.channelBadge, enabled && styles.channelBadgeOn)}
          aria-pressed={enabled}
          aria-label={ariaLabel}
          disabled={pending || !connected}
          onClick={handleClick}
        >
          {pending ? (
            <span className={styles.channelBadgeSpinner} aria-hidden="true" />
          ) : (
            <Icon size={16} aria-hidden="true" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent sideOffset={6}>{tooltipLabel}</TooltipContent>
    </Tooltip>
  );
});

type Toast = { id: number; message: string; tone: "success" | "error" };

export function AdminNotificationsPage() {
  useDocumentTitle("Notifications");

  const query = usePageSearch("Search by name or email…");

  // The whole filter+sort state (`filterTypes`/`filterChannels`/user-column
  // sort) lives in the URL's query string, not local `useState` — see this
  // block's own extended doc comment further down (kept where it was,
  // right above `toggleTypeFilter`/etc., since it explains those setters
  // too) for why. Decoded here, ABOVE `useSearchUsers`, because the raw
  // `types`/`channels`/`sort` search-param STRINGS (not the decoded `Map`s)
  // are what actually get sent to the BFF now — filtering/sorting moved
  // server-side (see notifications-admin-api.ts's `UserSearchFilters`), so
  // this page no longer re-filters/re-sorts `users` itself; it just passes
  // the URL's own param strings straight through.
  const [searchParams, setSearchParams] = useSearchParams();
  const typesParam = searchParams.get("types");
  // The MCP channel is only offered (and only honoured from a deep link)
  // for users with the `mcp_events_channel` flag.
  const mcpEventsEnabled = useMcpEventsEnabled();
  const channelsParam = sanitiseChannelsParam(searchParams.get("channels"), mcpEventsEnabled);
  const sortParam = searchParams.get("sort");
  const filterTypes = useMemo(() => decodeFilterMap<string>(typesParam), [typesParam]);
  const filterChannels = useMemo(() => decodeFilterMap<ChannelType>(channelsParam), [channelsParam]);
  const sortDirection = useMemo(() => decodeSort(sortParam), [sortParam]);

  // `users` IS the matrix's row set, already filtered/sorted by the BFF for
  // whatever `typesParam`/`channelsParam`/`sortParam` currently are — no
  // client-side re-filtering happens below any more (see the removed
  // `users` this replaced). `isFetching && isPlaceholderData` is the
  // "a NEW filtered/sorted request is in flight, but `keepPreviousData` is
  // still showing the OLD result set" case specifically — NOT a same-key
  // background refetch — see `useSearchUsers`'s own doc comment and
  // `<LoadingBar>`'s usage below.
  const {
    data: searchData,
    isLoading: searchLoading,
    isFetching: searchFetching,
    isPlaceholderData: searchIsPlaceholderData,
  } = useSearchUsers(query, { types: typesParam, channels: channelsParam, sort: sortParam });
  const users = searchData?.users ?? [];
  const userIds = useMemo(() => users.map((user) => user.id), [users]);
  const usersById = useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);
  const showLoadingBar = searchFetching && searchIsPlaceholderData;

  const { ref: matrixScrollRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const groupShadingFiller = useGroupShadingFiller(matrixScrollRef, users);

  const results = useUsersPreferences(userIds);
  const channelResults = useUsersChannels(userIds);
  const setPreference = useSetPreferenceForUser();
  const bulkApplyPreference = useBulkApplyPreference();

  const isCellEnabled = (userId: string, type: string, channel: ChannelType): boolean => {
    const index = userIds.indexOf(userId);
    const preferences = results[index]?.data?.preferences ?? [];
    return preferences.some(
      (pref) =>
        pref.notification_type === type &&
        pref.channel_type === channel &&
        pref.reference_id === null &&
        pref.enabled,
    );
  };

  // Setters for the URL-sourced filter/sort state decoded above
  // (`filterTypes`/`filterChannels`/`sortDirection`) — `useSearchParams()`
  // itself is the single source of truth here; those decoded values are
  // just DECODED views of it, recomputed on every `searchParams` change
  // rather than kept as separate state that could drift from the URL.
  const setTypeFilterModes = (modes: Map<string, MultiFilterMode>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const encoded = encodeFilterMap(modes);
        if (encoded) next.set("types", encoded);
        else next.delete("types");
        return next;
      },
      { replace: true },
    );
  const setChannelFilterModes = (modes: Map<ChannelType, MultiFilterMode>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const encoded = encodeFilterMap(modes);
        if (encoded) next.set("channels", encoded);
        else next.delete("channels");
        return next;
      },
      { replace: true },
    );
  const toggleUserSort = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const nextDirection = cycleSort(decodeSort(prev.get("sort")));
        if (nextDirection) next.set("sort", `${SORT_COLUMN_NAME}:${nextDirection}`);
        else next.delete("sort");
        return next;
      },
      { replace: true },
    );

  // Row multi-select — a checkbox per user, shift-click extends from the
  // last-clicked row to the clicked one (`lastCheckedIndex`), same
  // interaction shape as a file manager's row selection. Selecting ≥1 user
  // switches badge clicks from "toggle this one cell" to "apply this
  // toggle to every selected user's same (type, channel)" — see
  // `toggleChannel` below. Indexed against `users` (the BFF's own
  // already-filtered-and-sorted result — see the doc comment above
  // `useSearchUsers` for why there's no separate client-filtered list any
  // more) — shift-click and select-all only ever need to reach whatever's
  // currently on screen; a user filtered out keeps whatever selection state
  // it already had (selecting doesn't require staying
  // visible — the "Edit" bulk-edit button applies to every selected id
  // regardless of the current filter).
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const lastCheckedIndex = useRef<number | null>(null);

  const toggleUserSelected = (userId: string, index: number, shiftKey: boolean) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (shiftKey && lastCheckedIndex.current !== null) {
        const [start, end] = [lastCheckedIndex.current, index].sort((a, b) => a - b);
        const shouldSelect = !next.has(userId);
        for (let i = start; i <= end; i += 1) {
          const id = users[i]?.id;
          if (!id) continue;
          if (shouldSelect) next.add(id);
          else next.delete(id);
        }
      } else if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
    lastCheckedIndex.current = index;
  };

  const allSelected =
    users.length > 0 && users.every((user) => selectedUserIds.has(user.id));
  const toggleSelectAll = () => {
    setSelectedUserIds((prev) => {
      if (allSelected) {
        // Deselect only the currently-visible users — a selection made
        // under a different (or no) filter shouldn't vanish just because
        // this toggle only ever looks at what's on screen right now.
        const next = new Set(prev);
        for (const user of users) next.delete(user.id);
        return next;
      }
      return new Set([...prev, ...users.map((user) => user.id)]);
    });
  };

  const selectedVisibleCount = users.filter((user) => selectedUserIds.has(user.id)).length;
  const selectionSummary = searchLoading
    ? null
    : selectedVisibleCount > 0
      ? `${selectedVisibleCount} of ${users.length} selected`
      : `${users.length} user${users.length === 1 ? "" : "s"}`;

  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const secondaryHeaderContent = useMemo(
    () => (
      <div className={styles.secondaryHeaderRow}>
        <MultiFilterDropdown
          options={ALL_TYPE_OPTIONS}
          modes={filterTypes}
          onModesChange={setTypeFilterModes}
          triggerLabel={filterTriggerLabel(filterTypes.size, "type")}
          label="Filter by notification type"
        />
        <MultiFilterDropdown
          options={channelOptions(mcpEventsEnabled)}
          modes={filterChannels}
          onModesChange={setChannelFilterModes}
          triggerLabel={filterTriggerLabel(filterChannels.size, "channel")}
          label="Filter by channel"
        />
        <span className={styles.secondaryHeaderSpacer} />
        <button
          type="button"
          className={styles.editButton}
          disabled={selectedUserIds.size === 0}
          onClick={() => setBulkEditOpen(true)}
        >
          <Pencil size={14} aria-hidden="true" />
          Edit
        </button>
      </div>
    ),
    [filterTypes, filterChannels, selectedUserIds.size, mcpEventsEnabled],
  );
  useSecondaryHeader(secondaryHeaderContent);

  // Only WhatsApp/Slack/push require a real row in `notification.channel`
  // (an explicit external address/webhook the user registered) — email
  // uses the member's own primary email (always present, no separate
  // channel record needed) and in-app is inherent to being a member at
  // all (nothing external to "connect"). Disabling those two would be
  // wrong for every single user, not a real unconnected state.
  const isChannelConnected = (userId: string, channel: ChannelType): boolean => {
    if (channel === "email" || channel === "in_app") return true;
    const index = userIds.indexOf(userId);
    const channels = channelResults[index]?.data?.channels ?? [];
    return channels.some((userChannel) => userChannel.channel_type === channel);
  };

  const isRowLoading = (userId: string): boolean => {
    const index = userIds.indexOf(userId);
    return (results[index]?.isLoading || channelResults[index]?.isLoading) ?? true;
  };

  // Toasts — no toast system exists anywhere in this codebase yet, so this
  // is a small, page-local one (fixed bottom-right stack, auto-dismiss)
  // rather than inventing an app-wide primitive nobody asked for.
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextToastId = useRef(0);
  const pushToast = (message: string, tone: Toast["tone"]) => {
    const id = nextToastId.current;
    nextToastId.current += 1;
    setToasts((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => setToasts((prev) => prev.filter((toast) => toast.id !== id)), 3000);
  };

  // Per-cell pending state — `setPreference` is one shared mutation object
  // (bulk-apply can fire many calls from one click), so "is THIS cell
  // loading" can't just read `setPreference.isPending` (that's global to
  // every cell at once); track which cell keys are in flight explicitly.
  const cellKey = (userId: string, type: string, channel: ChannelType) => `${userId}:${type}:${channel}`;
  const [pendingCellKeys, setPendingCellKeys] = useState<Set<string>>(new Set());

  const applyChannelEdit = (userId: string, type: string, channel: ChannelType, enabled: boolean) => {
    const key = cellKey(userId, type, channel);
    setPendingCellKeys((prev) => new Set(prev).add(key));
    setPreference.mutate(
      { userId, edit: { type, refId: NULL_REF_ID, channel, enabled } },
      {
        onSuccess: () => {
          const name = usersById.get(userId) ? userDisplayName(usersById.get(userId)!) : userId;
          pushToast(`${enabled ? "Enabled" : "Disabled"} ${CHANNEL_LABEL[channel]} for ${name}`, "success");
        },
        onError: () => {
          const name = usersById.get(userId) ? userDisplayName(usersById.get(userId)!) : userId;
          pushToast(`Couldn't update ${CHANNEL_LABEL[channel]} for ${name}`, "error");
        },
        onSettled: () => {
          setPendingCellKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        },
      },
    );
  };

  const applyBulkChannelEdit = (
    targetUserIds: string[],
    type: string,
    channel: ChannelType,
    enabled: boolean,
  ) => {
    const keys = targetUserIds.map((userId) => cellKey(userId, type, channel));
    setPendingCellKeys((prev) => {
      const next = new Set(prev);
      for (const key of keys) next.add(key);
      return next;
    });
    bulkApplyPreference.mutate(
      { userIds: targetUserIds, edit: { type, refId: NULL_REF_ID, channel, enabled } },
      {
        onSuccess: () => {
          pushToast(
            `${enabled ? "Enabled" : "Disabled"} ${CHANNEL_LABEL[channel]} for ${targetUserIds.length} user${targetUserIds.length === 1 ? "" : "s"}`,
            "success",
          );
        },
        onError: () => {
          pushToast(
            `Couldn't update ${CHANNEL_LABEL[channel]} for ${targetUserIds.length} user${targetUserIds.length === 1 ? "" : "s"}`,
            "error",
          );
        },
        onSettled: () => {
          setPendingCellKeys((prev) => {
            const next = new Set(prev);
            for (const key of keys) next.delete(key);
            return next;
          });
        },
      },
    );
  };

  const toggleChannel = (userId: string, type: string, channel: ChannelType) => {
    const targetEnabled = !isCellEnabled(userId, type, channel);
    if (selectedUserIds.size > 0) {
      applyBulkChannelEdit(Array.from(selectedUserIds), type, channel, targetEnabled);
    } else {
      applyChannelEdit(userId, type, channel, targetEnabled);
    }
  };

  // `toggleChannel` itself closes over `selectedUserIds` (a new identity on
  // every selection change), which would otherwise defeat every single
  // `ChannelBadgeButton`'s `memo()` on every selection click even though
  // almost none of them actually change. Routing calls through a ref keeps
  // the FUNCTION REFERENCE passed down as `onToggle` permanently stable
  // while still always calling the current `toggleChannel` closure.
  const toggleChannelRef = useRef(toggleChannel);
  toggleChannelRef.current = toggleChannel;
  const stableToggleChannel = useCallback((userId: string, type: string, channel: ChannelType) => {
    toggleChannelRef.current(userId, type, channel);
  }, []);

  return (
    <div className={styles.page}>
      <PageSection
        className={styles.card}
        aria-label="User notification preferences"
        overlay={showLoadingBar && <LoadingBar topOffset={MATRIX_HEADER_HEIGHT} />}
      >
        {/* `.scrollShadowWrapper` > `.matrixScroll` (the ref `useScrollEdges`
            attaches its scroll listener to) is rendered UNCONDITIONALLY,
            unlike the skeleton/empty/table content it wraps — attaching the
            ref to a div that only mounts once data has loaded would leave
            `useScrollEdges`'s one-time mount effect holding a stale null
            ref, since the effect that (re-)binds the scroll listener never
            re-runs after this component's own first render (confirmed live:
            the shadow never appeared, `atTop` never left its default
            `true`). */}
        <div className={styles.scrollShadowWrapper}>
          {/* `data-at-top`/`data-at-bottom` drive `.userTd`/`.checkboxTd`'s
              own pinned-column shadow (see their doc comment in
              index.module.css) — the SAME `atTop`/`atBottom` the real
              `<ScrollShadow>` below already uses, just also exposed here as
              data attributes since that shadow can't reach these sticky
              cells via z-index (a browser limitation, not a CSS mistake —
              confirmed live: even z-index 999 on the external shadow div
              never wins against a sticky `<td>`'s own painting). */}
          <div
            className={styles.matrixScroll}
            ref={matrixScrollRef}
            data-at-top={atTop}
            data-at-bottom={atBottom}
          >
            {searchLoading ? (
              /* Shaped like the real matrix (real headers + real column
                 grid), not a generic stack of full-width bars — the
                 previous `.skeletonStack` (8 plain 36px bars stacked with
                 no column structure) read as its own, much wider/taller
                 unrelated placeholder next to the actual matrix that
                 replaces it, reported live as "too big". Reuses `<MatrixHead>`
                 so the skeleton's header is pixel-identical to the real one
                 that immediately follows it — a select-all checkbox that
                 hasn't loaded any users yet is disabled rather than hidden,
                 same idea as every other still-real, non-skeleton header. */
              <table className={styles.table}>
                <MatrixHead allSelected={false} onToggleSelectAll={() => {}} selectAllDisabled />
                <tbody>
                  {Array.from({ length: 8 }, (_, index) => (
                    <tr key={index} className={styles.row} aria-hidden="true">
                      <td className={cn(styles.td, styles.checkboxTd)}>
                        <SkeletonBlock width={14} height={14} radius="3px" />
                      </td>
                      <td className={cn(styles.td, styles.userTd)}>
                        <div className={styles.userCell}>
                          <SkeletonBlock
                            width={CHANNEL_BADGE_SIZE}
                            height={CHANNEL_BADGE_SIZE}
                            radius="var(--radius-full)"
                          />
                          <span className={styles.userMeta}>
                            <SkeletonBlock width={120} height={13} />
                            <SkeletonBlock width={90} height={11} />
                          </span>
                        </div>
                      </td>
                      {FLAT_TYPES.map((type) => (
                        <Fragment key={type.type}>
                          {needsSpacerBefore(type) && <td className={styles.spacer} />}
                          <td className={cn(styles.innerGap, type.groupIndex % 2 === 1 && styles.altColumn)} />
                          <td
                            className={cn(
                              styles.td,
                              type.groupIndex % 2 === 1 && styles.altColumn,
                              type.isGroupStart && styles.groupStart,
                            )}
                          >
                            <div className={styles.channelBadgeRow}>
                              {visibleChannels(type.channels, mcpEventsEnabled).map((channel) => (
                                <SkeletonBlock
                                  key={channel}
                                  width={CHANNEL_BADGE_SIZE}
                                  height={CHANNEL_BADGE_SIZE}
                                  radius="var(--radius-xs)"
                                />
                              ))}
                            </div>
                          </td>
                          <td className={cn(styles.innerGap, type.groupIndex % 2 === 1 && styles.altColumn)} />
                        </Fragment>
                      ))}
                      <td className={styles.trailingFill} />
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : users.length === 0 ? (
              // One empty state now, not two — the old "search returned
              // users but the type/channel filter narrowed them to zero"
              // case used to be distinguishable client-side (the unfiltered
              // list was already in hand); now that filtering happens
              // server-side, an empty result is just an empty result
              // whatever combination of search text/filters produced it, so
              // the message just names whichever of those is actually
              // active rather than assuming search vs. filter caused it.
              <p className={styles.emptyState}>
                {query.trim() || filterTypes.size > 0 || filterChannels.size > 0
                  ? "No users match your search and filters."
                  : "No users to show."}
              </p>
            ) : (
            <table className={styles.table}>
              <MatrixHead
                allSelected={allSelected}
                indeterminate={selectedVisibleCount > 0 && !allSelected}
                onToggleSelectAll={toggleSelectAll}
                selectionSummary={selectionSummary}
                sortDirection={sortDirection}
                onToggleSort={toggleUserSort}
              />
              <tbody>
                {users.map((user, index) => {
                  const loading = isRowLoading(user.id);
                  return (
                    <tr key={user.id} className={styles.row}>
                      <td className={cn(styles.td, styles.checkboxTd)}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={selectedUserIds.has(user.id)}
                          onChange={(event) =>
                            toggleUserSelected(
                              user.id,
                              index,
                              (event.nativeEvent as MouseEvent).shiftKey ?? false,
                            )
                          }
                          aria-label={`Select ${userDisplayName(user)}`}
                        />
                      </td>
                      <td className={cn(styles.td, styles.userTd)}>
                        <div className={styles.userCell}>
                          <NzrAvatar
                            name={userDisplayName(user)}
                            imageUrl={user.avatar}
                            size={CHANNEL_BADGE_SIZE}
                          />
                          <span className={styles.userMeta}>
                            <span className={styles.userName}>{userDisplayName(user)}</span>
                            {user.email && <span className={styles.userEmail}>{user.email}</span>}
                          </span>
                        </div>
                      </td>
                      {FLAT_TYPES.map((type) => (
                        <Fragment key={type.type}>
                          {needsSpacerBefore(type) && <td className={styles.spacer} />}
                          <td className={cn(styles.innerGap, type.groupIndex % 2 === 1 && styles.altColumn)} />
                          <td
                            className={cn(
                              styles.td,
                              type.groupIndex % 2 === 1 && styles.altColumn,
                              type.isGroupStart && styles.groupStart,
                            )}
                          >
                            {loading ? (
                              <div className={styles.channelBadgeRow}>
                                {visibleChannels(type.channels, mcpEventsEnabled).map((channel) => (
                                  <SkeletonBlock
                                    key={channel}
                                    width={30}
                                    height={30}
                                    radius="var(--radius-xs)"
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className={styles.channelBadgeRow}>
                                {visibleChannels(type.channels, mcpEventsEnabled).map((channel) => {
                                  const enabled = isCellEnabled(user.id, type.type, channel);
                                  const pending = pendingCellKeys.has(cellKey(user.id, type.type, channel));
                                  const connected = isChannelConnected(user.id, channel);
                                  return (
                                    <ChannelBadgeButton
                                      key={channel}
                                      userId={user.id}
                                      type={type.type}
                                      channel={channel}
                                      Icon={CHANNEL_ICON[channel]}
                                      enabled={enabled}
                                      pending={pending}
                                      connected={connected}
                                      ariaLabel={
                                        connected
                                          ? `${enabled ? "Disable" : "Enable"} ${type.label} (${CHANNEL_LABEL[channel]}) for ${userDisplayName(user)}`
                                          : `${userDisplayName(user)} has no ${CHANNEL_LABEL[channel]} channel connected`
                                      }
                                      tooltipLabel={
                                        connected ? CHANNEL_LABEL[channel] : `No ${CHANNEL_LABEL[channel]} connected`
                                      }
                                      onToggle={stableToggleChannel}
                                    />
                                  );
                                })}
                              </div>
                            )}
                          </td>
                          <td className={cn(styles.innerGap, type.groupIndex % 2 === 1 && styles.altColumn)} />
                        </Fragment>
                      ))}
                      <td className={styles.trailingFill} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
            {/* Extends the table's own alternating group shading (and the
                header/body boundary border, via `.groupShadingFiller`'s own
                border-top) down to fill whatever space real rows don't use
                — see `useGroupShadingFiller`'s own doc comment for why this
                has to be a sibling `<div>` (flex-filled) rather than a
                `<tr>` inside the table. */}
            <div
              className={styles.groupShadingFiller}
              style={{ background: groupShadingFiller.background, width: groupShadingFiller.width }}
              aria-hidden="true"
            />
          </div>
          {/* `zIndex={2}` — this matrix, unlike a plain `<MaximisedTablePage>`
              table, pins a checkbox/user column via `position: sticky` on
              BODY cells too (`.checkboxTd`/`.userTd`, z-index 1), not just
              the header. Those sticky cells sit in the same scrolling area
              the shadow overlays, and their opaque background otherwise
              painted over (hid) the shadow anywhere it crossed that column
              — even though that column is genuinely scrolling content, just
              horizontally pinned. 2 clears the body cells' z-index 1 while
              staying below the sticky `<thead>` itself (3). */}
          <ScrollShadow
            atTop={atTop}
            atBottom={atBottom}
            topOffset={MATRIX_HEADER_HEIGHT}
            zIndex={2}
          />
        </div>
      </PageSection>

      {toasts.length > 0 && (
        <div className={styles.toastStack} aria-live="polite">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={cn(styles.toast, toast.tone === "error" && styles.toastError)}
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}

      {/* The secondary header's "Edit" button (disabled with no selection —
          see `secondaryHeaderContent` above) — bulk-applies one or more
          (type, channel) → enabled/disabled edits to every selected user at
          once, reusing the same `applyBulkChannelEdit`/`useBulkApplyPreference`
          machinery a single badge click already uses when ≥1 row is
          selected (see `toggleChannel`), just looped once per chosen pair
          since the BFF's `bulk-apply` route only ever takes one
          type/channel/enabled triple per request (see
          notifications-admin-api.ts's own doc comment on that route). */}
      {bulkEditOpen && (
        <BulkEditModal
          mcpEventsEnabled={mcpEventsEnabled}
          selectedCount={selectedUserIds.size}
          onCancel={() => setBulkEditOpen(false)}
          onApply={(pairs: BulkEditPair[], enabled: boolean) => {
            const targetUserIds = Array.from(selectedUserIds);
            for (const pair of pairs) {
              applyBulkChannelEdit(targetUserIds, pair.type, pair.channel, enabled);
            }
            setBulkEditOpen(false);
          }}
        />
      )}
    </div>
  );
}
